import { useSuspenseQuery } from '@apollo/client';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  Button,
  Heading,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import { EvalSourceArtifactTypeLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';

import { AddSourceArtifactToDatasetDialog } from './AddSourceArtifactToDatasetDialog';
import { ArtifactDatasetsList } from './ArtifactDatasetsList';
import { EditArtifactFactsForm } from './EditArtifactFactsForm';
import { RelatedArtifactsList } from './RelatedArtifactsList';

const getArtifactDetailsQuery = graphql(`
  query getArtifactDetails($sourceArtifactId: uuid!) {
    evalSourceArtifactByPk(id: $sourceArtifactId) {
      __typename
      id
      type
      name
      description
      content
      createdAt
      sourceArtifactAppointment {
        __typename
        tenantId
        appointmentId
        patientId
        userId
      }
      datasetSourceArtifacts {
        __typename
        id
        createdAt
        dataset {
          __typename
          id
          name
          description
        }
      }
    }
  }
`);

export const ArtifactDetailsPage: React.FC = () => {
  const toast = useToast();
  const { artifactId } = useParams() as {
    artifactId: string;
  };

  const { pathname } = useLocation();
  const isEluveEval = pathname.includes('/eval');

  const {
    data: { evalSourceArtifactByPk: artifactData },
  } = useSuspenseQuery(getArtifactDetailsQuery, {
    variables: {
      sourceArtifactId: artifactId,
    },
  });

  if (!artifactData) {
    return null;
  }

  const appt = artifactData.sourceArtifactAppointment;

  return (
    <Stack gap={5} align="stretch">
      <div className="my-2 py-2">
        <Heading>{artifactData?.name}</Heading>
        <Stack gap={2} align="stretch">
          <Text className="text-black">{artifactData?.description}</Text>
          <Text> {formatToLocale(artifactData.createdAt)}</Text>
        </Stack>
      </div>

      <Stack gap={2} align="stretch">
        <Heading size="2xl" weight="semibold" className="pb-1">
          Details
        </Heading>
        <Text>
          Type: <span className="text-black">{artifactData.type}</span>
        </Text>
        <Text>
          Hashed Organization ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.tenantId}
          </span>
        </Text>
        <Text>
          Hashed Appointment ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.appointmentId}
          </span>
        </Text>
        <Text>
          Hashed User ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.userId}
          </span>
        </Text>
        <Text>
          Hashed Patient ID:{' '}
          <span className="text-black">
            {artifactData?.sourceArtifactAppointment?.patientId}
          </span>
        </Text>
      </Stack>
      <Stack className="md:flex-row" gap={5} align="stretch">
        <div className="w-full grow md:w-1/2">
          <Stack className="py-2" direction="horizontal" align="center" gap={4}>
            <Stack
              className="grow"
              direction="horizontal"
              align="center"
              gap={2}
              fullWidth={false}
            >
              <Heading size="2xl" weight="semibold" className="pb-1">
                Content
              </Heading>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(artifactData.content);
                  toast.success('Content copied to clipboard');
                }}
              >
                <Icon name="Copy" size="auto" />
              </Button>
            </Stack>

            <Text>
              <span className="text-black">
                {artifactData.content.length} Characters
              </span>
            </Text>
            <Text>
              <span className="text-black">
                ~{Math.ceil(artifactData.content.length / 4)} Tokens
              </span>
            </Text>
          </Stack>
          <Stack
            className="max-h-[800px] overflow-y-auto rounded-md bg-bg-quaternary p-5"
            gap={4}
            align="stretch"
          >
            <Text>{artifactData.content}</Text>
          </Stack>
        </div>
      </Stack>

      {/* Facts are only enabled for Transcripts */}
      {artifactData.type ===
        EvalSourceArtifactTypeLookup.DE_IDENTIFIED_TRANSCRIPT && (
        <div>
          <EditArtifactFactsForm sourceArtifactId={artifactId} />
        </div>
      )}

      {!isEluveEval && (
        <Stack gap={2} align="stretch">
          <Stack direction="horizontal" justify="between" align="stretch">
            <Heading size="2xl" weight="semibold" className="pb-1">
              Datasets
            </Heading>

            <AddSourceArtifactToDatasetDialog sourceArtifactId={artifactId} />
          </Stack>

          <ArtifactDatasetsList sourceArtifactId={artifactId} />
        </Stack>
      )}

      <div>
        <Heading size="2xl" weight="semibold" className="pb-1">
          Related Artifacts
        </Heading>
        {appt?.tenantId && appt?.appointmentId && (
          <RelatedArtifactsList
            tenantId={appt.tenantId}
            appointmentId={appt.appointmentId}
            excludeSourceArtifactId={artifactId}
          />
        )}
      </div>
    </Stack>
  );
};
